// 📁 src/pages/IncomingTransfersPage.jsx
import { useEffect, useState } from "react";
import { Table } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import "../styles/TableStyles.css";
import { CheckCircle2, PackageCheck, Info, XCircle } from "lucide-react";

export default function IncomingTransfersPage() {
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [detailRow, setDetailRow] = useState(null);

  const getUser = async () => {
    try {
      const res = await fetch("/api/protected/me", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const json = await res.json();
      return {
        pharmacyCode: json?.user?.pharmacyCode?.replace(/^0+/, ""),
        firma: json?.user?.firma,
      };
    } catch (e) {
      console.error("Chyba při načítání uživatele:", e);
      return null;
    }
  };

  const fetchData = async (firma, pharmacyCode, resetSelection = false) => {
    try {
      const res = await fetch(`/api/transfers/incoming/${firma}/${pharmacyCode}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const json = await res.json();
      setData(json);

      if (resetSelection) {
        const defaults = {};
        json.forEach((row) => {
          if (row.stav === "rezervováno") defaults[row._id] = true;
        });
        setSelected(defaults);
      }
    } catch (e) {
      console.error("Chyba načítání incoming:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const u = await getUser();
      setUser(u);
      if (u) fetchData(u.firma, u.pharmacyCode, true); // reset jen při prvním načtení
    })();
  }, []);

  const toggleRow = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const confirmSelection = async () => {
    if (!user) return;
    const ids = Object.keys(selected).filter((id) => selected[id]);
    if (!ids.length) return;
    try {
      await fetch(`/api/transfers/confirm/${user.firma}/${user.pharmacyCode}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ ids }),
      });
      fetchData(user.firma, user.pharmacyCode, false);
    } catch (e) {
      console.error("Chyba potvrzení:", e);
    }
  };

  const markReceived = async (id) => {
    if (!user) return;
    try {
      await fetch(`/api/transfers/markReceived/${user.firma}/${user.pharmacyCode}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ id }),
      });
      fetchData(user.firma, user.pharmacyCode, false);
    } catch (e) {
      console.error("Chyba při označení přijato:", e);
    }
  };

  const rejectRow = async (id) => {
    if (!user) return;
    try {
      await fetch(`/api/transfers/reject/${user.firma}/${user.pharmacyCode}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ id }),
      });
      fetchData(user.firma, user.pharmacyCode, false);
    } catch (e) {
      console.error("Chyba při odmítnutí:", e);
    }
  };

  const renderHistory = (history) => {
    if (!history || !history.length) return "–";
    return history
      .slice()
      .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
      .map(
        (h) =>
          `${new Date(h.changedAt).toLocaleString()} → ${h.stav}${
            h.note ? ` (${h.note})` : ""
          }`
      )
      .join("\n");
  };

  if (loading) return <p>Načítám…</p>;

  const sortedData = [...data].sort((a, b) => {
    const scoreA = (a.NCSD ?? 0) * ((a.probability ?? 0) / 2);
    const scoreB = (b.NCSD ?? 0) * ((b.probability ?? 0) / 2);
    return scoreB - scoreA;
  });

  return (
    <div className="page">
      <h1>Převodky – Příjem</h1>
      <Table className="styled-table">
        <thead>
          <tr>
            <th></th>
            <th>PDK</th>
            <th style={{ width: "35%" }}>Název</th>
            <th>Expirace</th>
            <th>Šarže</th>
            <th>Množství</th>
            <th style={{ width: "60px" }}>Zdroj</th>
            <th>Stav</th>
            <th style={{ width: "150px" }}>Akce</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row) => {
            const prob = row.probability !== undefined ? row.probability : null;
            const gradient =
              prob !== null
                ? `linear-gradient(to right, rgba(0,200,0,0.2) ${prob}%, white ${prob}%)`
                : "none";

            return (
              <tr key={row._id}>
                <td>
                  {row.stav === "rezervováno" && (
                    <input
                      type="checkbox"
                      checked={!!selected[row._id]}
                      onChange={() => toggleRow(row._id)}
                      style={{
                        width: "18px",
                        height: "18px",
                        accentColor: "green",
                      }}
                    />
                  )}
                </td>
                <td>{row.PDK}</td>
                <td style={{ background: gradient }}>{row.Název}</td>
                <td>{new Date(row.Exspirace).toLocaleDateString().replace(/\. /g, ".")}</td>
                <td>{row.Šarže}</td>
                <td>{row.Množství}</td>
                <td>{row.pharmacyCode}</td>
                <td>{row.stav}</td>
                <td>
                  <button
                    style={{
                      padding: "2px 6px",
                      fontSize: "12px",
                      background: "none",
                      border: "none",
                      color: "#007bff",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                    onClick={() => setDetailRow(row)}
                  >
                    <Info size={14} style={{ marginRight: "4px" }} />
                    Detail
                  </button>

                  {row.stav === "rezervováno" && (
                    <button
                      onClick={() => rejectRow(row._id)}
                      style={{
                        marginTop: "4px",
                        padding: "2px 6px",
                        fontSize: "12px",
                        background: "#dc3545",
                        border: "none",
                        color: "white",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <XCircle size={14} style={{ marginRight: "4px" }} />
                      Odmítnout
                    </button>
                  )}

                  {row.stav === "odesláno" && (
                    <button
                      onClick={() => markReceived(row._id)}
                      style={{
                        marginTop: "4px",
                        padding: "2px 6px",
                        fontSize: "12px",
                        background: "#007bff",
                        border: "none",
                        color: "white",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <PackageCheck size={14} style={{ marginRight: "4px" }} />
                      Označit přijato
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {data.some((row) => row.stav === "rezervováno") && (
        <div
          className="actions"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "10px",
            gap: "10px",
          }}
        >
          {/* 🆕 Tlačítko Zrušit výběr */}
          <button
            onClick={() => setSelected({})}
            className="px-4 py-2 rounded"
            style={{
              backgroundColor: "#6c757d",
              color: "white",
              fontSize: "16px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <XCircle size={18} style={{ marginRight: "6px" }} />
            Zrušit výběr
          </button>

          {/* Původní tlačítko Potvrdit */}
          <button
            onClick={confirmSelection}
            className="px-4 py-2 rounded"
            style={{
              backgroundColor: "green",
              color: "white",
              fontSize: "16px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <CheckCircle2 size={18} style={{ marginRight: "6px" }} />
            Potvrdit vybrané
          </button>
        </div>
      )}

      {detailRow && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "white",
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            zIndex: 1000,
            width: "500px",
            maxHeight: "80vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ flex: "1 1 auto", overflowY: "auto" }}>
            <h3>📦 Detail položky</h3>
            <p>Cena: {detailRow.NCSD ?? "?"} Kč</p>
            <p>
              Pravděpodobnost výdeje:{" "}
              {detailRow.probability !== undefined
                ? `${detailRow.probability}%`
                : "—"}
            </p>
            <h4 style={{ marginTop: "10px" }}>Historie:</h4>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                fontSize: "13px",
                fontWeight: "normal",
                color: "#444",
              }}
            >
              {renderHistory(detailRow.history)}
            </pre>
          </div>
          <div style={{ textAlign: "right", marginTop: "10px" }}>
            <button
              onClick={() => setDetailRow(null)}
              style={{
                background: "#007bff",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
